import { useEffect } from "react";

/**
 * SEO component for managing document title, meta descriptions,
 * canonical URLs, robots indexing instructions, Open Graph,
 * Twitter tags, and page-specific Schema.org JSON-LD structured data.
 *
 * Compatible with React 18 & 19 without external dependencies.
 */
const DEFAULT_TITLE = "Kisan Mithr – AI-Powered Agriculture Platform for Farmers";
const DEFAULT_DESCRIPTION =
  "Kisan Mithr is an AI-powered agriculture platform providing farmers and plant growers with smart agricultural assistance, crop guidance, weather information, market information, and farming support.";
const DEFAULT_IMAGE = "https://kisanmithr.in/logo.jpg";
const DEFAULT_URL = "https://kisanmithr.in/";
const SITE_NAME = "Kisan Mithr";

function setOrCreateMeta(selector, attribute, attrValue, content) {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, attrValue);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setOrCreateLink(rel, href) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  noindex = false,
  ogType = "website",
  ogImage = DEFAULT_IMAGE,
  jsonLd = null,
}) {
  useEffect(() => {
    // 1. Page Title
    const finalTitle = title ? title : DEFAULT_TITLE;
    document.title = finalTitle;

    // 2. Primary Meta Description
    setOrCreateMeta('meta[name="description"]', "name", "description", description);
    setOrCreateMeta('meta[name="title"]', "name", "title", finalTitle);

    // 3. Robots Directives
    const robotsContent = noindex
      ? "noindex, nofollow"
      : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";
    setOrCreateMeta('meta[name="robots"]', "name", "robots", robotsContent);

    // 4. Canonical Link
    const finalCanonical = canonical || DEFAULT_URL;
    setOrCreateLink("canonical", finalCanonical);

    // 5. Open Graph Meta Tags
    setOrCreateMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME);
    setOrCreateMeta('meta[property="og:type"]', "property", "og:type", ogType);
    setOrCreateMeta('meta[property="og:title"]', "property", "og:title", finalTitle);
    setOrCreateMeta('meta[property="og:description"]', "property", "og:description", description);
    setOrCreateMeta('meta[property="og:url"]', "property", "og:url", finalCanonical);
    setOrCreateMeta('meta[property="og:image"]', "property", "og:image", ogImage);
    setOrCreateMeta('meta[property="og:locale"]', "property", "og:locale", "en_IN");

    // 6. Twitter Meta Tags
    setOrCreateMeta('meta[property="twitter:card"]', "property", "twitter:card", "summary_large_image");
    setOrCreateMeta('meta[property="twitter:title"]', "property", "twitter:title", finalTitle);
    setOrCreateMeta('meta[property="twitter:description"]', "property", "twitter:description", description);
    setOrCreateMeta('meta[property="twitter:url"]', "property", "twitter:url", finalCanonical);
    setOrCreateMeta('meta[property="twitter:image"]', "property", "twitter:image", ogImage);

    // 7. Page-Specific Schema.org JSON-LD
    const SCRIPT_ID = "kisanmithr-page-jsonld";
    let scriptTag = document.getElementById(SCRIPT_ID);

    if (jsonLd) {
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.id = SCRIPT_ID;
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(jsonLd);
    } else if (scriptTag) {
      scriptTag.remove();
    }

    return () => {
      // Cleanup page-specific JSON-LD on unmount
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) existing.remove();
    };
  }, [title, description, canonical, noindex, ogType, ogImage, jsonLd]);

  return null;
}
